import React, { useEffect, useState } from "react";
import { ShieldCheck, Link2, AlertCircle, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_API_BASE || "http://127.0.0.1:8000";
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
  "Content-Type": "application/json",
});

const card     = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const inputCls = "w-full bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors";
const labelCls = "text-xs text-[#00ff90]/50 uppercase tracking-wider block mb-1.5";

const PAY_FREQUENCIES = ["Monthly", "Fortnightly", "Weekly", "FourWeekly", "Quarterly", "Annual"];

// Backend wraps every response in the ok() envelope: {ok, message, data}.
// Read the payload from .data, and never assume a shape we didn't get.
const unwrap = (body) => (body && typeof body === "object" && "data" in body ? body.data : body);

// FastAPI raises HTTPException(400, detail) -> {"detail": "..."}. Pull the
// human message out of whatever came back rather than showing "[object Object]".
function errorText(body, res) {
  const d = body?.detail ?? body?.message;
  if (typeof d === "string" && d.trim()) return d;
  if (Array.isArray(d) && d.length) return d.map(x => x?.msg || String(x)).join("; ");
  return `Request failed (HTTP ${res?.status ?? "?"}).`;
}

async function call(path, { method = "GET", body } = {}) {
  const res = await fetch(`${API}${path}`, {
    method,
    headers: authHeaders(),
    body: body ? JSON.stringify(body) : undefined,
  });
  let parsed = null;
  try { parsed = await res.json(); } catch { /* empty/non-JSON body */ }
  if (!res.ok) throw new Error(errorText(parsed, res));
  return unwrap(parsed);
}

function StatusPill({ connected }) {
  return connected ? (
    <span className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border border-[#00ff90]/30 bg-[#00ff90]/10 text-[#00ff90]">
      <ShieldCheck size={12} /> Connected
    </span>
  ) : (
    <span className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border border-white/15 bg-white/5 text-white/40">
      Payroll not set up
    </span>
  );
}

export default function PayrollSetupTab() {
  const [setup,   setSetup]   = useState(null);
  const [form,    setForm]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [error,   setError]   = useState(null);
  const [notice,  setNotice]  = useState(null);

  const hydrate = (data) => {
    setSetup(data);
    setForm({
      legal_name:    data?.company?.legal_name    || "",
      address_line1: data?.company?.address_line1 || "",
      address_line2: data?.company?.address_line2 || "",
      city:          data?.company?.city          || "",
      postcode:      data?.company?.postcode      || "",
      country:       data?.company?.country       || "",
      paye_reference:            data?.hmrc?.paye_reference            || "",
      accounts_office_reference: data?.hmrc?.accounts_office_reference || "",
      pay_frequency: data?.pay_frequency || "Monthly",
    });
  };

  const load = async () => {
    setLoading(true); setError(null);
    try { hydrate(await call("/api/admin/payroll/setup")); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  // Load once on mount. `load` is stable enough for this — re-creating it
  // each render would re-fetch on every keystroke in the form.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const doSave = async () => {
    setSaving(true); setError(null); setNotice(null);
    try {
      hydrate(await call("/api/admin/payroll/setup", { method: "PUT", body: form }));
      setNotice({ type: "success", text: "Setup saved." });
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const doConnect = async () => {
    setConnecting(true); setError(null); setNotice(null);
    try {
      const data = await call("/api/admin/payroll/connect", { method: "POST" });
      hydrate(data);
      setNotice({
        type: "success",
        text: data?.already_connected
          ? "Payroll was already connected — nothing was changed."
          : "Payroll connected.",
      });
    } catch (e) { setError(e.message); }
    finally { setConnecting(false); }
  };

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  if (loading) {
    return (
      <div className={`${card} p-8 flex items-center justify-center gap-2 text-white/30 text-sm`}>
        <Loader2 size={14} className="animate-spin" /> Loading payroll setup…
      </div>
    );
  }

  // A hard load failure leaves no form to edit — show the reason, not a blank card.
  if (!form) {
    return (
      <div className={`${card} p-6 space-y-3`}>
        <p className="text-red-400 text-sm flex items-center gap-2">
          <AlertCircle size={14} /> {error || "Payroll setup could not be loaded."}
        </p>
        <button onClick={load} className="text-xs px-4 py-1.5 rounded-lg border border-white/10 text-white/50 hover:text-white transition-colors">
          Retry
        </button>
      </div>
    );
  }

  const connected = !!setup?.connected;
  const missing   = setup?.missing_fields || [];

  return (
    <div className="space-y-5">
      <div className={card}>
        <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
          <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Payroll Setup</span>
          <StatusPill connected={connected} />
        </div>

        <div className="p-5 space-y-5">
          <p className="text-xs text-white/30 leading-relaxed">
            Set up your company for UK payroll. These details are used to register your
            company with our payroll provider and appear on your HMRC submissions.
          </p>

          {/* ── Company ── */}
          <div>
            <p className="text-xs text-white/40 uppercase tracking-wider mb-3">Company</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className={labelCls}>Legal name</label>
                <input className={inputCls} value={form.legal_name} onChange={set("legal_name")}
                  placeholder="As registered at Companies House" />
              </div>
              <div><label className={labelCls}>Address line 1</label>
                <input className={inputCls} value={form.address_line1} onChange={set("address_line1")} /></div>
              <div><label className={labelCls}>Address line 2</label>
                <input className={inputCls} value={form.address_line2} onChange={set("address_line2")} /></div>
              <div><label className={labelCls}>City</label>
                <input className={inputCls} value={form.city} onChange={set("city")} /></div>
              <div><label className={labelCls}>Postcode</label>
                <input className={inputCls} value={form.postcode} onChange={set("postcode")} /></div>
              <div><label className={labelCls}>Country</label>
                <input className={inputCls} value={form.country} onChange={set("country")}
                  placeholder="England / Scotland / Wales / Northern Ireland" /></div>
              <div><label className={labelCls}>Pay frequency</label>
                <select className={inputCls} value={form.pay_frequency} onChange={set("pay_frequency")}>
                  {PAY_FREQUENCIES.map(f => <option key={f}>{f}</option>)}
                </select></div>
            </div>
          </div>

          {/* ── HMRC ── */}
          <div>
            <p className="text-xs text-white/40 uppercase tracking-wider mb-1.5">HMRC references</p>
            <p className="text-xs text-white/25 mb-3 leading-relaxed">
              These references are issued to your company by HMRC. You are responsible
              for their accuracy — incorrect references will cause your payroll
              submissions to be rejected.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>PAYE reference</label>
                <input className={inputCls} value={form.paye_reference} onChange={set("paye_reference")}
                  placeholder="123/AB456" />
                <p className="text-xs text-white/20 mt-1">Found on your P30BC or PAYE letters.</p>
              </div>
              <div>
                <label className={labelCls}>Accounts Office reference</label>
                <input className={inputCls} value={form.accounts_office_reference}
                  onChange={set("accounts_office_reference")} placeholder="123PA00012345" />
                <p className="text-xs text-white/20 mt-1">13 characters, issued with your PAYE registration.</p>
              </div>
            </div>
          </div>

          {error  && <p className="text-red-400 text-xs flex items-start gap-1.5"><AlertCircle size={13} className="shrink-0 mt-0.5" /> {error}</p>}
          {notice && <p className={`text-xs ${notice.type === "success" ? "text-[#00ff90]" : "text-red-400"}`}>{notice.text}</p>}

          <div className="flex gap-2 justify-end pt-1">
            <button onClick={load} disabled={saving || connecting}
              className="text-xs px-3 py-1.5 rounded-lg border border-white/10 text-white/40 hover:text-white disabled:opacity-40 transition-colors">
              Reset
            </button>
            <button onClick={doSave} disabled={saving || connecting}
              className="text-xs px-4 py-1.5 rounded-lg bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30 hover:bg-[#00ff90]/25 disabled:opacity-40 transition-colors font-medium">
              {saving ? "Saving…" : "Save details"}
            </button>
          </div>
        </div>
      </div>

      {/* ── Connection ── */}
      <div className={card}>
        <div className="px-5 py-4 border-b border-white/5 flex items-center gap-2">
          <Link2 size={13} className="text-[#00ff90]/60" />
          <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Connection</span>
        </div>
        <div className="p-5 space-y-3">
          {connected ? (
            <>
              <p className="text-sm text-white/60">
                Your company is connected and ready for payroll.
              </p>
              {setup?.employer_reference && (
                <p className="text-xs text-white/25">
                  Employer reference: <span className="font-mono text-white/40">{setup.employer_reference}</span>
                  <span className="text-white/15"> — quote this to support if you need help.</span>
                </p>
              )}
              <p className="text-xs text-white/20">
                Already connected. Connecting again won't create a second payroll company.
              </p>
            </>
          ) : (
            <>
              <p className="text-sm text-white/60">
                Your company isn't set up for payroll yet.
              </p>
              {missing.length > 0 && (
                <div className="text-xs text-amber-400/70 border border-amber-500/20 bg-amber-500/5 rounded-lg px-3 py-2">
                  <p className="mb-1 font-medium">Before you can connect, add:</p>
                  <ul className="list-disc list-inside space-y-0.5 text-amber-400/60">
                    {missing.map(m => <li key={m}>{m}</li>)}
                  </ul>
                </div>
              )}
              <button onClick={doConnect} disabled={connecting || missing.length > 0}
                className="text-xs px-5 py-2 rounded-lg bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30 hover:bg-[#00ff90]/25 disabled:opacity-40 disabled:cursor-not-allowed transition-colors font-medium">
                {connecting ? "Connecting…" : "Connect payroll"}
              </button>
              <p className="text-xs text-white/20">
                This registers your company with our payroll provider. It does not file
                anything with HMRC.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
